import React from "react";
import { Minus, Plus, Trash2, ShoppingCart, UtensilsCrossed } from "lucide-react";
import useNav from "./hooks/useNav";
import PrimaryButton from "./components/PrimaryButton";
import Header from "./components/Header";
import BackButton from "./components/BackButton";
import { useCart } from "./context/CartContext";
import { useRestaurant } from "./context/RestaurantContext";

const CartPage = () => {
    const { cartItems, increaseQuantity, decreaseQuantity, removeItem, totalPrice } = useCart();
    const { restaurant } = useRestaurant();
    const navigate = useNav();

    return (
        <div className="bg-gray-50 min-h-screen">
            <Header restaurantName={restaurant?.name} />

            <div className="max-w-screen-md mx-auto px-4 py-6 page-enter">
                {/* Назад + Заголовок */}
                <div className="flex items-center gap-3 mb-5">
                    <BackButton onClick={() => navigate("/")} />
                    <h1 className="text-xl font-bold text-gray-900">Корзина</h1>
                    {cartItems.length > 0 && (
                        <span className="ml-auto text-sm text-gray-400">
                            {cartItems.length} {cartItems.length === 1 ? 'позиция' : cartItems.length < 5 ? 'позиции' : 'позиций'}
                        </span>
                    )}
                </div>

                {cartItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                        <ShoppingCart size={52} className="text-gray-200 mb-4" />
                        <p className="text-lg font-semibold text-gray-500">Корзина пуста</p>
                        <p className="text-sm mt-1 mb-6 text-gray-400">Добавьте блюда из меню</p>
                        <PrimaryButton onClick={() => navigate("/")}>
                            Перейти в меню
                        </PrimaryButton>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {cartItems.map((item) => (
                            <div
                                key={item.id}
                                className="flex items-center gap-3 bg-white p-3.5 rounded-2xl shadow-sm border border-gray-100"
                            >
                                {/* Фото */}
                                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                                    {item.image ? (
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.classList.add('fallback'); }}
                                        />
                                    ) : (
                                        <UtensilsCrossed size={24} className="text-gray-300" />
                                    )}
                                </div>

                                {/* Название + цена */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold text-gray-900 leading-tight truncate">
                                        {item.title}
                                    </h3>
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        {item.price} ₽ × {item.quantity} = <span className="font-semibold text-gray-700">{(item.price * item.quantity).toFixed(0)} ₽</span>
                                    </p>
                                </div>

                                {/* Степпер */}
                                <div className="flex items-center gap-1 bg-gray-50 rounded-xl border border-gray-200 p-0.5">
                                    <button
                                        onClick={() => decreaseQuantity(item.id)}
                                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors"
                                    >
                                        <Minus size={13} strokeWidth={2.5} />
                                    </button>
                                    <span className="text-sm font-bold text-gray-900 min-w-[18px] text-center">
                                        {item.quantity}
                                    </span>
                                    <button
                                        onClick={() => increaseQuantity(item.id)}
                                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-brandBlue text-white hover:bg-brandBlue/90 transition-colors"
                                    >
                                        <Plus size={13} strokeWidth={2.5} />
                                    </button>
                                </div>

                                {/* Удалить */}
                                <button
                                    onClick={() => removeItem(item.id)}
                                    className="p-1.5 text-gray-300 hover:text-red-400 transition-colors"
                                    aria-label="Удалить"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {cartItems.length > 0 && (
                    <div className="mt-6 space-y-4">
                        <div className="flex justify-between items-center px-1">
                            <span className="text-gray-600">Итого</span>
                            <span className="text-xl font-bold text-gray-900">{totalPrice.toFixed(0)} ₽</span>
                        </div>

                        <PrimaryButton
                            className="w-full py-3.5 text-base font-semibold"
                            onClick={() => navigate("/order")}
                        >
                            Оформить заказ
                        </PrimaryButton>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CartPage;
